import React, { useState, useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { scaleLinear } from "@visx/scale";
import { Area } from "@visx/shape";
import { curveBasis } from "@visx/curve";
import { LinearGradient } from "@visx/gradient";
import {
  EngagementFunnelPanelProps,
  FunnelSegment,
} from "./EnagementFunnelPanel.types";
import { useEngagementFunnelPanel } from "./EnagementFunnelPanel.hooks";
import {
  Box,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import ComponentLoader from "../../components/ComponentLoader/ComponentLoader";
import { Panel } from "../../components";
import { formatNumber } from "../../utils/formatUtils";
import PNRatio from "../PNRatio/PNRatio";

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`engagement-tabpanel-${index}`}
      aria-labelledby={`engagement-tab-${index}`}
      {...other}
      style={{ height: "100%" }}
    >
      {value === index && <Box sx={{ height: "100%" }}>{children}</Box>}
    </Box>
  );
}

function EngagementFunnelPanel(props: EngagementFunnelPanelProps) {
  const theme = useTheme();

  const {
    isLoading,
    segments,
    interpolatedData,
    tabValue,
    handleTabChange,
    stageInfo,
    lastPeriod,
    pnratio,
    isPNRatioLoading,
  } = useEngagementFunnelPanel(props);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  function FunnelChart({ width, height }: { width: number; height: number }) {
    const chartTopPadding = 70;
    const chartBottomPadding = isMobile ? 90 : 70;
    const chartHeight = height - chartTopPadding - chartBottomPadding;

    const numSegments = segments.length - 1;
    const maxValue = Math.max(
      ...interpolatedData.map((d) => Math.max(d.value, 0.25))
    );
    const valuePadding = 50;
    const minmax = maxValue + valuePadding;

    const xScale = scaleLinear({
      range: [0, width],
      domain: [0, numSegments],
    });

    const yScale = scaleLinear({
      range: [chartHeight, 0],
      domain: [-minmax, minmax],
    });

    const areas = [
      { pad: 0, opacity: 1 },
      { pad: 15, opacity: 0.2 },
      { pad: 30, opacity: 0.1 },
    ];

    const getChangeColor = (value: number) => {
      if (value > 0) return theme.palette.success.main;
      if (value < 0) return theme.palette.error.main;
      return theme.palette.text.secondary;
    };

    return (
      <svg
        width={width}
        height={height}
        style={{ animation: "fadeIn 1s ease-out" }}
      >
        <LinearGradient
          id="gradient"
          from={theme.palette.primary.light}
          to={theme.palette.primary.dark}
          vertical={false}
        />

        {[1, 2, 3].map((i) => (
          <line
            key={`divider-${i}`}
            x1={(width / 4) * i}
            x2={(width / 4) * i}
            y1={0}
            y2={height}
            stroke={theme.palette.divider}
            strokeWidth={1}
          />
        ))}

        <g transform={`translate(0, ${chartTopPadding})`}>
          {areas.map((area, i) => (
            <Area
              key={`area-${i}`}
              data={interpolatedData}
              curve={curveBasis}
              x={(d: FunnelSegment) => xScale(d.index)}
              y0={(d: FunnelSegment) => yScale(d.value + area.pad)}
              y1={(d: FunnelSegment) => yScale(-d.value - area.pad)}
              fill="url(#gradient)"
              fillOpacity={area.opacity}
              stroke="transparent"
            />
          ))}
        </g>

        {stageInfo.map((stage, i) => {
          const stageX = (width / stageInfo.length) * (i + 0.5);
          return (
            <g key={`icon-${i}`}>
              <circle
                cx={stageX}
                cy={30}
                r={16}
                fill={theme.palette.primary.main}
                fillOpacity={0.1}
              />
              <path
                d={stage.icon}
                transform={`translate(${stageX - 12}, ${18})`}
                stroke={theme.palette.primary.main}
                strokeWidth="1.5"
                fill="none"
              />
              <text
                x={stageX}
                y={65}
                fontSize={isMobile ? 10 : 12}
                fontWeight="bold"
                textAnchor="middle"
                fill={theme.palette.text.primary}
              >
                {stage.label}
              </text>
            </g>
          );
        })}

        {stageInfo.map((stage, i) => {
          const stageX = (width / stageInfo.length) * (i + 0.5);
          return (
            <text
              key={`value-${i}`}
              x={stageX}
              y={chartTopPadding + chartHeight / 2}
              fontSize={18}
              fontWeight="bold"
              textAnchor="middle"
              fill={theme.palette.text.primary}
            >
              {`${stage.value.toFixed(1)}%`}
            </text>
          );
        })}

        {stageInfo.map((stage, i) => {
          const stageX = (width / stageInfo.length) * (i + 0.5);
          const changeColor = getChangeColor(stage.change);
          const changePrefix = stage.change > 0 ? "+" : "";
          return (
            <g key={`footer-${i}`}>
              <text
                x={stageX}
                y={height - (isMobile ? 50 : 30)}
                fontSize={14}
                fontWeight="bold"
                textAnchor="middle"
                fill={theme.palette.text.primary}
              >
                {formatNumber(stage.count)}
              </text>
              <text
                x={stageX}
                y={height - (isMobile ? 30 : 10)}
                fontSize={12}
                textAnchor="middle"
              >
                {isMobile ? (
                  <>
                    <tspan x={stageX} dy={0} fill={changeColor}>
                      {`${changePrefix}${stage.change.toFixed(1)}%`}
                    </tspan>
                    <tspan
                      x={stageX}
                      dy={18}
                      fill={theme.palette.text.secondary}
                    >
                      {`vs last ${lastPeriod}`}
                    </tspan>
                  </>
                ) : (
                  <>
                    <tspan fill={changeColor}>
                      {`${changePrefix}${stage.change.toFixed(1)}% `}
                    </tspan>
                    <tspan fill={theme.palette.text.secondary}>
                      {`vs last ${lastPeriod}`}
                    </tspan>
                  </>
                )}
              </text>
            </g>
          );
        })}
      </svg>
    );
  }

  return (
    <Panel
      title={props?.title || "Engagement funnel"}
      actions={
        !props?.email && (
          <PNRatio value={pnratio || 0} isLoading={isPNRatioLoading} />
        )
      }
    >
      {isLoading ? (
        <ComponentLoader height="415px" />
      ) : (
        <Box sx={{ width: "100%", height: isMobile ? 450 : 400 }}>
          {!props?.email && (
            <Box sx={{ borderBottom: 1, borderColor: "divider" }} mt={4} px={5}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="engagement funnel tabs"
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="All" />
                <Tab label="Campaigns" />
                <Tab label="Workflows" />
              </Tabs>
            </Box>
          )}
          {props?.email ? (
            <>
              <Divider sx={{ mt: 4 }} />
              <ParentSize>
                {({ width, height }: { width: number; height: number }) => (
                  <FunnelChart
                    width={width}
                    height={height - (isMobile ? 40 : 32)}
                  />
                )}
              </ParentSize>
            </>
          ) : (
            [0, 1, 2].map((index) => (
              <TabPanel key={index} value={tabValue} index={index}>
                <ParentSize>
                  {({ width, height }: { width: number; height: number }) => (
                    <FunnelChart
                      width={width}
                      height={height - (isMobile ? 40 : 32)}
                    />
                  )}
                </ParentSize>
              </TabPanel>
            ))
          )}
        </Box>
      )}
    </Panel>
  );
}

export default EngagementFunnelPanel;
